'use client';

import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useTranslations } from 'next-intl';
import { useQuickToast } from 'hooks/use-toast';
import {
  AddToCartApiCall,
  getPaymentMethods
} from 'services/parts-diagram/actions';
import {
  CATEGORY_TYPES,
  ComponentsType,
  LineItem,
  PartDetailsAndSchematicResponse,
  PartItem,
  AnalyticsCommonInformation
} from 'types/parts-diagram';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import { getFormattedItemData } from 'components/product-comparison-grid/product-comparison-card/utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import {
  extractNumericIndex,
  parsePriceToFloat
} from 'app/[lang]/[esite]/(core)/product/[product]/_components/anchor-menu/utils';
import { GA_PDP_DIAGRAM } from 'constants/analyticsConstants';
interface FormattedPriceData extends ProductDealerPriceAvailability {
  type: string;
  name: string;
  imageURL: string;
  index: number;
}

interface PartItemWithQuantity extends LineItem {
  quantity: string;
  sosCode?: string;
}

interface PartDiagramContext {
  isToggle: boolean;
  handleToggle: () => void;
  displayCategory: CATEGORY_TYPES;
  handlePartsContentCategory: (
    category: CATEGORY_TYPES,
    components?: ComponentsType | null,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => void;
  componentsData: ComponentsType;
  partsDetails: PartDetailsAndSchematicResponse;
  selectedPart: PartItem;
  handleSelectedPart: (partItem: PartItem) => void;
  isCatCorp: boolean;
  setIsCatCorp: Dispatch<SetStateAction<boolean>>;
  handleAddToCart: (
    part: LineItem,
    quantity: string,
    analyticsInformation?: AnalyticsCommonInformation
  ) => void;
  equipmentError: boolean;
  setEquipmentError: Dispatch<SetStateAction<boolean>>;
  disableAddToCart: boolean;
  serverData;
  setServerData;
  clickedLineItem: LineItem;
  setClickedLineItem: Dispatch<SetStateAction<LineItem>>;
  analyticsCommonInfo?: AnalyticsCommonInformation;
  setAnalyticsCommonInfo?: Dispatch<SetStateAction<AnalyticsCommonInformation>>;
}

const PartDiagramContext = createContext<PartDiagramContext>(null);
export const usePartDiagram = () => useContext(PartDiagramContext);

export const PartDiagramContextProvider = ({ children }) => {
  const t = useTranslations();
  const toast = useQuickToast();
  const [isToggle, setIsToggle] = useState(false);
  const [displayCategory, setDisplayCategory] = useState<CATEGORY_TYPES>(
    CATEGORY_TYPES.LISTOFARRANGEMENTS
  );
  const [componentsData, setComponentsData] = useState<ComponentsType>();
  const [partsDetails, setPartsDetails] =
    useState<PartDetailsAndSchematicResponse>();
  const [selectedPart, setSelectedPart] = useState<PartItem | null>(null);
  const [isCatCorp, setIsCatCorp] = useState<boolean>(true);
  const [equipmentError, setEquipmentError] = useState<boolean>(false);
  const [disableAddToCart, setDisableAddToCart] = useState<boolean>(false);
  const [serverData, setServerData] = useState();

  const [analyticsCommonInfo, setAnalyticsCommonInfo] =
    useState<AnalyticsCommonInformation>({});
  const [clickedLineItem, setClickedLineItem] = useState<LineItem>();

  const handleToggle = useCallback(() => setIsToggle(!isToggle), [isToggle]);

  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();

  const handleSelectedPart = useCallback(
    (partItem: PartItem) => setSelectedPart(partItem),
    []
  );

  const handlePartsContentCategory = (
    category: CATEGORY_TYPES,
    componentsData?: ComponentsType,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => {
    setDisplayCategory(category);
    switch (category) {
      case CATEGORY_TYPES.PARTSCARDS:
        setComponentsData(componentsData);
        break;
      case CATEGORY_TYPES.PARTSDETAILS:
        setPartsDetails(partsDetails);
        break;
    }
  };

  const createFormattedPriceData = (part: LineItem) => {
    return {
      ...part.priceAndAvailabilityData,
      type: '',
      name: part.name,
      imageURL: '',
      index: parseInt(part?.referenceNumber)
    };
  };

  const calculateDiscountInfo = (priceData: FormattedPriceData) => {
    const discountedPrice = parsePriceToFloat(priceData?.discountedPrice);
    const totalPriceForDiscount = parsePriceToFloat(priceData?.totalPrice);
    const hasDiscount =
      discountedPrice > 0 && discountedPrice < totalPriceForDiscount;
    const discountValue = hasDiscount
      ? totalPriceForDiscount - discountedPrice
      : 0;

    return { discountValue, hasDiscount };
  };

  const createSelectItemEventData = (
    partItem: PartItemWithQuantity,
    orderId: string,
    priceData: FormattedPriceData,
    analyticsInformation?: AnalyticsCommonInformation
  ) => {
    return {
      item: {
        orderItemId: orderId,
        partNumber: partItem.partNumber,
        quantity: priceData.quantity,
        availabilityMessage:
          priceData?.availability?.[0]?.availabilityMessage || '',
        totalPrice: priceData?.unformattedTotalPrice
          ? Number(priceData.unformattedTotalPrice)
          : 0
      },
      index: parseInt(partItem?.referenceNumber || '0'),
      partName:
        partItem?.partNumber && partItem?.name
          ? `${partItem.partNumber}: ${partItem.name}`
          : '',
      locationId: '',
      itemSummary: {
        categoryPath: priceData.categoryPath,
        manufacturer: analyticsInformation?.manufacturer
      },
      isSearchPageEvent: true,
      itemListId: analyticsInformation?.EventListInfo,
      itemListName: analyticsInformation?.EventListInfo
    };
  };

  const createItemSummaryData = (
    partItem: PartItemWithQuantity,
    priceData: FormattedPriceData,
    analyticsInformation?: AnalyticsCommonInformation,
    discountValue = 0
  ) => {
    const diagramNumber = extractNumericIndex(partItem?.referenceNumber);
    const calculatedIndex = diagramNumber ? diagramNumber - 1 : undefined;

    return {
      manufacturer: analyticsInformation?.manufacturer,
      index: calculatedIndex,
      categoryPath: priceData.categoryPath,
      quantity: priceData?.quantity,
      partNumber: priceData?.partNumber,
      name: partItem?.name,
      unformattedTotalPrice: priceData?.unformattedTotalPrice,
      availability: priceData?.availability,
      productPriceData: {
        ...priceData,
        price: {
          discountAmount: discountValue
        }
      }
    };
  };

  async function handleAddToCart(
    part: LineItem,
    quantity: string,
    analyticsInformation?: AnalyticsCommonInformation
  ) {
    setDisableAddToCart(true);

    const partItem: PartItemWithQuantity = {
      ...part,
      quantity: quantity,
      sosCode: part?.priceAndAvailabilityData?.sosCode
    };

    const { error, response } = await AddToCartApiCall(partItem);

    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
      setDisableAddToCart(false);
      return;
    }

    toast.success({
      message: t('ITEM_ADDED_TO_CART')
    });

    const { orderId } = response;
    const { paymentMethods } = await getPaymentMethods(orderId);

    if (paymentMethods) {
      const priceAndAvailabilityFormatedData = createFormattedPriceData(part);

      const selectItemEventData = createSelectItemEventData(
        partItem,
        orderId,
        priceAndAvailabilityFormatedData,
        analyticsInformation
      );
      fireSelectItemEvent(selectItemEventData);

      const { discountValue } = calculateDiscountInfo(
        priceAndAvailabilityFormatedData
      );
      const itemSummaryData = createItemSummaryData(
        partItem,
        priceAndAvailabilityFormatedData,
        analyticsInformation,
        discountValue
      );

      addToCartItemEvent({
        locationId: '',
        index: parseInt(part?.referenceNumber),
        itemSummary: itemSummaryData,
        itemData: getFormattedItemData(
          orderId,
          priceAndAvailabilityFormatedData,
          analyticsInformation?.currency
        ),
        itemListId: analyticsInformation?.EventListInfo || GA_PDP_DIAGRAM,
        itemListName: analyticsInformation?.EventListInfo || GA_PDP_DIAGRAM
      });
    }

    setDisableAddToCart(false);
  }

  const contextValues = useMemo(() => {
    return {
      isToggle,
      handleToggle,
      displayCategory,
      handlePartsContentCategory,
      componentsData,
      partsDetails,
      selectedPart,
      handleSelectedPart,
      isCatCorp,
      setIsCatCorp,
      handleAddToCart,
      equipmentError,
      setEquipmentError,
      disableAddToCart,
      serverData,
      setServerData,
      analyticsCommonInfo,
      setAnalyticsCommonInfo,
      clickedLineItem,
      setClickedLineItem
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isToggle,
    handleToggle,
    displayCategory,
    componentsData,
    partsDetails,
    selectedPart,
    handleSelectedPart,
    isCatCorp,
    setIsCatCorp,
    equipmentError,
    setEquipmentError,
    disableAddToCart,
    serverData,
    setServerData,
    analyticsCommonInfo,
    setAnalyticsCommonInfo,
    clickedLineItem,
    setClickedLineItem
  ]);

  return (
    <PartDiagramContext.Provider value={contextValues}>
      {children}
    </PartDiagramContext.Provider>
  );
};
